import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Response, User } from '../models';
import { UserRole } from '../models/user-roles.enum';
import { UserSettings } from '../models/user-settings.model';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  redirectUrl: string = '';

  constructor(
    private httpClient: HttpClient,
    private router: Router) { }

  public isAuthenticated(): boolean {
    const token = localStorage.getItem('v2.token');
    if (!token) {
      return false;
    }

    const decodedToken: JwtPayload = jwtDecode(token);
    const currentTime = Math.floor(Date.now() / 1000);
    return currentTime < decodedToken.exp!;
  }

  public externalLogin(idToken: string, provider: string): Observable<Response<string>> {
    const url: string = `${environment.apiEndpoint}/login/ExternalLogin`;
    return this.httpClient.post(url, { idToken, provider });
  }

  public logout(): void {
    this.clearCookies();
    localStorage.clear();
    sessionStorage.clear();
  }

  public setUser(token: string): void {
    if (token) {
      localStorage.setItem('v2.token', token);
      const decodedToken: User = jwtDecode<User>(token);
      const user: User = {
        id: decodedToken.id,
        accountId: decodedToken.accountId,
        email: decodedToken.email,
        firstName: decodedToken.firstName,
        lastName: decodedToken.lastName,
        login: decodedToken.login,
        role: decodedToken.role ?? []
      };
      localStorage.setItem('v2.user', JSON.stringify(user));
    }
  }

  public getUser(): any {
    const userJsonString = localStorage.getItem('v2.user');
    if (userJsonString) {
      try {
        return JSON.parse(userJsonString) as User
      } catch (error) {
        return null;
      }
    }
    return null;
  }

  public setUserSettings(settings: UserSettings): any {
    if (settings) {
      localStorage.setItem('v2.userSettings', JSON.stringify(settings))
    }
  }

  public getUserSettings(): UserSettings | null {
    const userSettingsJsonString = localStorage.getItem('v2.userSettings');

    if (userSettingsJsonString) {
      try {
        return JSON.parse(userSettingsJsonString) as UserSettings
      } catch (error) {
        return null
      }
    }
    return null
  }

  public hasRole(role: UserRole) {
    const user: User = this.getUser();
    return user && user.role?.some(x => x === role);
  }

  public hasAnyRole(roles: UserRole[]) {
    const user: User = this.getUser();
    return user && user.role?.some(x => roles.includes(x));
  }

  public getAccessToken(): string {
    const result = localStorage.getItem('v2.token') ?? '';
    return result;
  }

  redirect(): void {
    if (this.redirectUrl) {
      this.router.navigateByUrl(this.redirectUrl);
    } else {
      const url = this.isAuthenticated() ? '/home' : '/login';
      this.router.navigateByUrl(url);
    }
  }

  private clearCookies(): void {
    document.cookie.replace(/(?<=^|;).+?(?=\=|;|$)/g, name => location.hostname.split('.').reverse().reduce(domain => (domain = domain.replace(/^\.?[^.]+/, ''), document.cookie = `${name}=;max-age=0;path=/;domain=${domain}`, domain), location.hostname));
  }
}
